<template>
    <v-card flat class="pa-4">
        <div class="text-subtitle-2 mb-2">
            <v-icon small class="mr-1">mdi-account-group</v-icon>
            Capacidades
        </div>
        <div class="text-caption grey--text mb-4">
            Defina as capacidades e selecione qual está ativa para o cálculo atual.
        </div>
        
        <v-row align="stretch">
            <v-col cols="12" sm="4">
                <v-card
                    outlined
                    :class="['capacity-card', { 'capacity-card--active': capacityMode === 'RED' }]"
                    @click="selectMode('RED')"
                >
                    <v-card-text class="text-center pa-3">
                        <v-radio-group
                            :value="capacityMode"
                            hide-details
                            class="mt-0 mb-2"
                            @change="selectMode"
                        >
                            <v-radio
                                value="RED"
                                class="justify-center"
                            >
                                <template v-slot:label>
                                    <span class="text-subtitle-2">Reduzida</span>
                                </template>
                            </v-radio>
                        </v-radio-group>

                        <div class="active-slot mb-2">
                            <v-chip
                                x-small
                                color="primary"
                                :class="{ 'active-slot__chip--hidden': capacityMode !== 'RED' }"
                            >
                                Ativo
                            </v-chip>
                        </div>

                        <div class="capacity-desc text-caption grey--text mb-2">
                            Capacidade mínima
                        </div>

                        <v-text-field
                            :value="reducedCapacity"
                            @input="onReducedCapacityChange"
                            type="number"
                            min="0"
                            outlined
                            dense
                            class="centered-input"
                            :error-messages="displayErrors.reduced_capacity"
                        />

                        <div class="text-caption grey--text mt-2">
                            lugares
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" sm="4">
                <v-card
                    outlined
                    :class="['capacity-card', { 'capacity-card--active': capacityMode === 'CONV' }]"
                    @click="selectMode('CONV')"
                >
                    <v-card-text class="text-center pa-3">
                        <v-radio-group
                            :value="capacityMode"
                            hide-details
                            class="mt-0 mb-2"
                            @change="selectMode"
                        >
                            <v-radio
                                value="CONV"
                                class="justify-center"
                            >
                                <template v-slot:label>
                                    <span class="text-subtitle-2">Convencional</span>
                                </template>
                            </v-radio>
                        </v-radio-group>

                        <div class="active-slot mb-2">
                            <v-chip
                                x-small
                                color="primary"
                                :class="{ 'active-slot__chip--hidden': capacityMode !== 'CONV' }"
                            >
                                Ativo
                            </v-chip>
                        </div>

                        <div class="capacity-desc text-caption grey--text mb-2">
                            Capacidade real padrão
                        </div>

                        <v-text-field
                            :value="capacity"
                            @input="onCapacityChange"
                            type="number"
                            min="1"
                            outlined
                            dense
                            class="centered-input"
                            :error-messages="displayErrors.capacity"
                        />

                        <div class="text-caption grey--text mt-2">
                            lugares
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" sm="4">
                <v-card
                    outlined
                    :class="['capacity-card', { 'capacity-card--active': capacityMode === 'AMP' }]"
                    @click="selectMode('AMP')"
                >
                    <v-card-text class="text-center pa-3">
                        <v-radio-group
                            :value="capacityMode"
                            hide-details
                            class="mt-0 mb-2"
                            @change="selectMode"
                        >
                            <v-radio
                                value="AMP"
                                class="justify-center"
                            >
                                <template v-slot:label>
                                    <span class="text-subtitle-2">Ampliada</span>
                                </template>
                            </v-radio>
                        </v-radio-group>

                        <div class="active-slot mb-2">
                            <v-chip
                                x-small
                                color="primary"
                                :class="{ 'active-slot__chip--hidden': capacityMode !== 'AMP' }"
                            >
                                Ativo
                            </v-chip>
                        </div>

                        <div class="capacity-desc text-caption grey--text mb-2">
                            Máximo de segurança
                        </div>

                        <v-text-field
                            :value="extendedCapacity"
                            @input="onExtendedCapacityInput"
                            @focus="extendedFocused = true"
                            @blur="onExtendedCapacityBlur"
                            type="number"
                            min="0"
                            outlined
                            dense
                            class="centered-input"
                            :error-messages="displayErrors.extended_capacity"
                        />

                        <div class="text-caption grey--text mt-2">
                            lugares
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row v-if="showActiveCapacity" class="mt-2">
            <v-col cols="12">
                <v-alert type="info" dense text>
                    <strong>Capacidade ativa:</strong> {{ activeCapacityValue }} lugares ({{ activeCapacityLabel }})
                </v-alert>
            </v-col>
        </v-row>
    </v-card>
</template>

<script>
export default {
    name: 'SysAmbienceCapacitySelector',

    data() {
        return {
            extendedFocused: false,
            extendedTouched: false
        }
    },
    
    props: {
        capacity: {
            type: [Number, String],
            default: 0
        },
        extendedCapacity: {
            type: [Number, String],
            default: 0
        },
        reducedCapacity: {
            type: [Number, String],
            default: 0
        },
        capacityMode: {
            type: String,
            default: 'CONV'
        },
        showActiveCapacity: {
            type: Boolean,
            default: true
        },
        errors: {
            type: Object,
            default: () => ({})
        }
    },

    computed: {
        activeCapacityValue() {
            switch (this.capacityMode) {
                case 'RED':
                    return Number.parseInt(this.reducedCapacity) || 0
                case 'AMP':
                    return Number.parseInt(this.extendedCapacity) || 0
                default:
                    return Number.parseInt(this.capacity) || 0
            }
        },

        activeCapacityLabel() {
            switch (this.capacityMode) {
                case 'RED':
                    return 'Reduzida'
                case 'AMP':
                    return 'Ampliada'
                default:
                    return 'Convencional'
            }
        },

        /**
         * Valida a tríade de capacidades no cliente (CT-CAP-01).
         *
         * Why: feedback imediato no formulário, sem depender do round-trip
         * pro backend. Backend continua sendo a autoridade (UpdateAmbienceRequest),
         * mas a UI não espera erro vir de lá pra mostrar.
         */
        localValidationErrors() {
            const errors = {}
            const capacity = Number.parseInt(this.capacity) || 0
            const extended = Number.parseInt(this.extendedCapacity) || 0
            const reduced = Number.parseInt(this.reducedCapacity) || 0

            if (capacity > 0 && extended > 0 && extended <= capacity) {
                errors.extended_capacity = 'Deve ser maior que a convencional.'
            }

            if (capacity > 0 && reduced > 0 && reduced >= capacity) {
                errors.reduced_capacity = 'Deve ser menor que a convencional.'
            }

            if (this.capacityMode === 'AMP' && extended <= 0) {
                errors.extended_capacity = 'Defina um valor para usar o modo Ampliada.'
            }

            if (this.capacityMode === 'RED' && reduced <= 0) {
                errors.reduced_capacity = 'Defina um valor para usar o modo Reduzida.'
            }

            return errors
        },

        /**
         * Erros do backend (prop `errors`) têm prioridade sobre os locais —
         * uma mensagem vinda do servidor é mais específica.
         *
         * Para extended_capacity: só mostra erro local quando o campo perdeu
         * o foco (touched) e não está sendo editado (focused), para evitar
         * mensagem de erro durante a digitação.
         */
        displayErrors() {
            const showExtendedLocalError = this.extendedTouched && !this.extendedFocused

            return {
                capacity: this.errors.capacity ?? null,
                extended_capacity: this.errors.extended_capacity ?? (showExtendedLocalError ? this.localValidationErrors.extended_capacity : null),
                reduced_capacity: this.errors.reduced_capacity ?? this.localValidationErrors.reduced_capacity ?? null,
            }
        },

        hasLocalErrors() {
            return Object.keys(this.localValidationErrors).length > 0
        }
    },

    watch: {
        localValidationErrors: {
            immediate: true,
            handler(val) {
                this.$emit('validation-change', Object.keys(val).length === 0)
            }
        },

        capacity() {
            if (!this.extendedFocused) {
                this.extendedTouched = false
            }
        }
    },

    methods: {
        selectMode(mode) {
            this.$emit('update:capacityMode', mode)
        },

        onCapacityChange(value) {
            this.$emit('update:capacity', Number.parseInt(value) || 0)
        },

        onReducedCapacityChange(value) {
            this.$emit('update:reducedCapacity', Number.parseInt(value) || 0)
        },

        /**
         * Atualiza o valor da capacidade ampliada durante a digitação.
         * O erro só será exibido após o campo perder o foco.
         */
        onExtendedCapacityInput(value) {
            this.$emit('update:extendedCapacity', Number.parseInt(value) || 0)
        },

        /**
         * Marca o campo como "touched" quando perde o foco,
         * permitindo que o erro de validação seja exibido.
         */
        onExtendedCapacityBlur() {
            this.extendedFocused = false
            this.extendedTouched = true
        }
    }
}
</script>

<style scoped>
/* Altura uniforme entre as três caixinhas: a v-col já estica (align="stretch"),
   o card preenche 100% dela e o conteúdo distribui em coluna. Sem isso, cada
   caixinha dimensiona pela própria altura e a troca de modo as faz pular. */
.capacity-card {
    cursor: pointer;
    /* Anima só cor/borda; nunca dimensões, pra não animar reflow. */
    transition: border-color 0.2s ease, background-color 0.2s ease;
    border: 2px solid transparent;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.capacity-card .v-card__text {
    flex: 1 1 auto;
}

.capacity-card:hover {
    border-color: #1976D2;
    background-color: rgba(25, 118, 210, 0.04);
}

.capacity-card--active {
    border-color: #1976D2;
    background-color: rgba(25, 118, 210, 0.08);
}

/* Espaço fixo do chip "Ativo": renderizado sempre e apenas escondido quando
   inativo, mantendo a altura reservada em todas as caixinhas. */
.active-slot {
    min-height: 20px;
}

.active-slot__chip--hidden {
    visibility: hidden;
}

/* Linha de descrição com altura reservada para alinhar os campos das três
   caixinhas mesmo quando o texto difere. */
.capacity-desc {
    min-height: 18px;
}

.centered-input >>> input {
    text-align: center;
    font-size: 1.5rem;
    font-weight: bold;
}

.v-radio {
    margin: 0 auto;
}
</style>
